import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getAllowedSources,
  isValidRadiusKm,
} from "../../../utils/recommendationEngine/constants";
import {
  astToDisplayString,
  walkFormulaSignals,
} from "../../../utils/recommendationEngine/formulaAst";
import {
  CandidateSourceId,
  CandidateSourceMetadata,
  RecommendationEngineEndpoint,
  RecommendationEngineEndpointConfig,
  RecommendationEngineSourceConfig,
  SignalMetadata,
} from "../../../types/recommendationEngine";
import FormulaEditorModal from "./components/FormulaEditorModal";
import StepSection from "./components/StepSection";
import CandidateSourceCard from "./components/CandidateSourceCard";
import FilterEditor from "./components/FilterEditor";
import RerankRulesEditor from "./components/RerankRulesEditor";
import InfoTooltip from "./components/InfoTooltip";
import {
  FIELD_TOOLTIPS,
  SELECTION_GUIDANCE_BULLETS,
  SIGNAL_CONCEPT_TOOLTIPS,
  STEP_LAYOUT_HEADERS,
} from "./copy";
import { engineTokens } from "./theme";

export type PipelineUpdater = (
  prev: RecommendationEngineEndpointConfig,
) => RecommendationEngineEndpointConfig;

type Props = {
  /** The endpoint whose rules the pipeline follows: its signals, sources and formula checks. */
  endpoint: RecommendationEngineEndpoint;
  pipeline: RecommendationEngineEndpointConfig;
  onChange: (updater: PipelineUpdater) => void;
  signals: SignalMetadata[];
  candidateSources: CandidateSourceMetadata[];
  /** Order of the source cards; defaults to the endpoint's own order. */
  sourceOrder?: CandidateSourceId[];
  /**
   * Fields shown in place of a source card's built-in params, for params the
   * catalog cannot describe. Return undefined to keep the built-in ones.
   */
  renderSourceParams?: (
    source: RecommendationEngineSourceConfig,
    onSourceChange: (next: RecommendationEngineSourceConfig) => void,
  ) => React.ReactNode | undefined;
  /** Shows the pipeline without letting anything in it be changed. */
  readOnly?: boolean;
};

const numberOrUndefined = (value: string): number | undefined =>
  value === "" ? undefined : Number(value);

/**
 * The editing steps of a recommendation engine pipeline: candidate sources,
 * filters, rerank rules, formula, display limits and post-processing. It edits
 * the pipeline it is given; loading and saving are up to the caller.
 */
const RecommendationEnginePipelineEditor = ({
  endpoint,
  pipeline: draft,
  onChange: updateDraft,
  signals,
  candidateSources: candidateSourceCatalog,
  sourceOrder,
  renderSourceParams,
  readOnly = false,
}: Props) => {
  const candidateSourceMetadataById = useMemo(() => {
    const map = new Map<CandidateSourceId, CandidateSourceMetadata>();
    candidateSourceCatalog.forEach((source) => {
      map.set(source.id, source);
    });
    return map;
  }, [candidateSourceCatalog]);

  const [formulaModalOpen, setFormulaModalOpen] = useState(false);
  const [guidanceOpen, setGuidanceOpen] = useState(false);

  // `inert` blocks every control, including MUI selects that a disabled
  // fieldset would not reach. React 18 has no prop for it.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contentRef.current?.toggleAttribute("inert", readOnly);
  }, [readOnly]);

  const endpointSignals = useMemo(
    () =>
      signals.filter((signal) =>
        signal.applicableEndpoints?.includes(endpoint),
      ),
    [endpoint, signals],
  );

  const distanceDecayRadius = draft.signalParams?.distance_decay?.radiusKm;

  // Show the distance-decay control when the signal is valid for this endpoint,
  // or when the saved config already carries a distance_decay entry (so an
  // existing value stays visible/editable even if the signal catalog hasn't
  // loaded yet). Sending the key to an endpoint that doesn't support it is a
  // backend 400, but every current endpoint uses distance_decay.
  const distanceDecayApplicable = useMemo(
    () =>
      endpointSignals.some((signal) => signal.id === "distance_decay") ||
      draft.signalParams?.distance_decay !== undefined,
    [endpointSignals, draft.signalParams],
  );

  const distanceDecayRadiusInvalid =
    distanceDecayRadius !== undefined && !isValidRadiusKm(distanceDecayRadius);

  const formulaSignals = useMemo(
    () => Array.from(walkFormulaSignals(draft.formula)),
    [draft.formula],
  );

  const rawFormulaSignals = useMemo(() => {
    const activeIds = new Set(formulaSignals);
    return endpointSignals.filter(
      (signal) => signal.isRaw && activeIds.has(signal.id),
    );
  }, [endpointSignals, formulaSignals]);

  const sourceRows = useMemo(() => {
    const allowed = sourceOrder ?? getAllowedSources(endpoint);
    const allowedSet = new Set(allowed);
    const sourceMap = new Map(
      draft.candidateSources.map((source) => [source.sourceId, source]),
    );
    const ordered = allowed
      .map((sourceId) => sourceMap.get(sourceId))
      .filter((source): source is RecommendationEngineSourceConfig =>
        Boolean(source),
      );
    // Render any sources the saved config carries that aren't in the hardcoded
    // order (e.g. backend added sources for this endpoint) so they stay editable.
    const extras = draft.candidateSources.filter(
      (source) => !allowedSet.has(source.sourceId),
    );
    return [...ordered, ...extras];
  }, [draft.candidateSources, endpoint, sourceOrder]);

  const updateSource = (
    sourceId: CandidateSourceId,
    nextSource: RecommendationEngineSourceConfig,
  ) => {
    updateDraft((prev) => ({
      ...prev,
      candidateSources: prev.candidateSources.map((source) =>
        source.sourceId === sourceId ? nextSource : source,
      ),
    }));
  };

  return (
    <Box ref={contentRef} aria-disabled={readOnly || undefined}>
      {/* Step 1: Candidate sources */}
      <StepSection
        step={1}
        title={STEP_LAYOUT_HEADERS[0].title}
        helper={STEP_LAYOUT_HEADERS[0].helper}
        action={
          <Button size="small" onClick={() => setGuidanceOpen((open) => !open)}>
            {guidanceOpen ? "Hide tuning guidance" : "Show tuning guidance"}
          </Button>
        }
      >
        <Collapse in={guidanceOpen} unmountOnExit>
          <Alert severity="info" sx={{ mb: 2 }}>
            <List dense disablePadding sx={{ pl: 2, listStyleType: "disc" }}>
              {SELECTION_GUIDANCE_BULLETS.map((bullet) => (
                <ListItem
                  key={bullet}
                  sx={{ display: "list-item", py: 0, pl: 0 }}
                >
                  <ListItemText
                    primary={bullet}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        </Collapse>

        {sourceRows.map((source) => {
          const onSourceChange = (next: RecommendationEngineSourceConfig) =>
            updateSource(source.sourceId, next);
          return (
            <CandidateSourceCard
              key={source.sourceId}
              source={source}
              metadata={candidateSourceMetadataById.get(source.sourceId)}
              onChange={onSourceChange}
              customParams={renderSourceParams?.(source, onSourceChange)}
            />
          );
        })}
      </StepSection>

      {/* Step 2: Filters */}
      <StepSection
        step={2}
        title={STEP_LAYOUT_HEADERS[1].title}
        helper={STEP_LAYOUT_HEADERS[1].helper}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.excludeSourceMerchant}
                onChange={(e) =>
                  updateDraft((p) => ({
                    ...p,
                    excludeSourceMerchant: e.target.checked,
                  }))
                }
              />
            }
            label="Implicit: exclude source merchant"
          />
          <InfoTooltip title={FIELD_TOOLTIPS.excludeSourceMerchant} />
        </Box>

        <FilterEditor
          filters={draft.filters}
          signals={endpointSignals}
          clamp01={draft.postProcess.clamp01}
          onChange={(filters) => updateDraft((p) => ({ ...p, filters }))}
        />
      </StepSection>

      {/* Step 3: Rerank rules */}
      <StepSection
        step={3}
        title={STEP_LAYOUT_HEADERS[2].title}
        helper={STEP_LAYOUT_HEADERS[2].helper}
      >
        <RerankRulesEditor
          rules={draft.rerankRules ?? []}
          onChange={(rerankRules) =>
            updateDraft((p) => ({ ...p, rerankRules }))
          }
        />
      </StepSection>

      {/* Step 4: Formula */}
      <StepSection
        step={4}
        title={STEP_LAYOUT_HEADERS[3].title}
        helper={STEP_LAYOUT_HEADERS[3].helper}
        action={
          readOnly ? undefined : (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setFormulaModalOpen(true)}
            >
              Edit formula
            </Button>
          )
        }
      >
        <Box
          sx={{
            backgroundColor: engineTokens.bgMuted,
            border: `1px solid ${engineTokens.border}`,
            borderRadius: 2,
            p: 1.5,
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              wordBreak: "break-word",
            }}
          >
            {astToDisplayString(draft.formula)}
          </Typography>
        </Box>

        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary">
            Distinct formula signals: {formulaSignals.length}
          </Typography>
          <InfoTooltip title={SIGNAL_CONCEPT_TOOLTIPS.distinctSignals} />
        </Box>

        {formulaSignals.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {formulaSignals.map((signalId) => {
              const signal = endpointSignals.find(
                (item) => item.id === signalId,
              );
              const tooltipBody = signal ? (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {signal.label}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {signal.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    Group: {signal.group} ·{" "}
                    {signal.isRaw ? "Raw count" : "0–1 normalized"}
                  </Typography>
                </Box>
              ) : (
                signalId
              );
              return (
                <Tooltip
                  key={signalId}
                  title={tooltipBody}
                  placement="top"
                  arrow
                >
                  <Chip
                    size="small"
                    label={signal?.label ?? signalId}
                    color={
                      signal?.isRaw && draft.postProcess.clamp01
                        ? "warning"
                        : "default"
                    }
                    variant="outlined"
                  />
                </Tooltip>
              );
            })}
          </Box>
        )}

        {draft.postProcess.clamp01 && rawFormulaSignals.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Raw signals in use while clamp 0-1 is enabled:{" "}
            {rawFormulaSignals.map((signal) => signal.label).join(", ")}.
            Consider lowering their weights so they do not dominate the score.
          </Alert>
        )}

        {distanceDecayApplicable && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <TextField
              size="small"
              type="number"
              label="Distance-decay radius (km)"
              value={distanceDecayRadius ?? ""}
              onChange={(e) =>
                updateDraft((p) => ({
                  ...p,
                  signalParams: {
                    ...(p.signalParams ?? {}),
                    distance_decay: {
                      ...(p.signalParams?.distance_decay ?? {}),
                      radiusKm: numberOrUndefined(e.target.value),
                    },
                  },
                }))
              }
              error={distanceDecayRadiusInvalid}
              helperText={
                distanceDecayRadiusInvalid ? "0 < km ≤ 100" : undefined
              }
              sx={{ width: 220 }}
            />
            <InfoTooltip title={FIELD_TOOLTIPS.distanceDecayRadius} />
          </Box>
        )}
      </StepSection>

      {/* Step 5: Display limits */}
      <StepSection
        step={5}
        title={STEP_LAYOUT_HEADERS[4].title}
        helper={STEP_LAYOUT_HEADERS[4].helper}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Display limit"
                value={draft.displayLimit}
                onChange={(e) =>
                  updateDraft((p) => ({
                    ...p,
                    displayLimit: Number(e.target.value),
                  }))
                }
              />
              <InfoTooltip title={FIELD_TOOLTIPS.displayLimit} />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Ranking window limit"
                value={draft.rankingWindowLimit}
                onChange={(e) =>
                  updateDraft((p) => ({
                    ...p,
                    rankingWindowLimit: Number(e.target.value),
                  }))
                }
              />
              <InfoTooltip title={FIELD_TOOLTIPS.rankingWindowLimit} />
            </Box>
          </Grid>
        </Grid>
      </StepSection>

      {/* Step 6: Post-process */}
      <StepSection
        step={6}
        title={STEP_LAYOUT_HEADERS[5].title}
        helper={STEP_LAYOUT_HEADERS[5].helper}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.postProcess.clamp01}
                onChange={(e) =>
                  updateDraft((p) => ({
                    ...p,
                    postProcess: {
                      ...p.postProcess,
                      clamp01: e.target.checked,
                    },
                  }))
                }
              />
            }
            label="Clamp scores 0–1"
          />
          <InfoTooltip title={FIELD_TOOLTIPS.clamp01} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.postProcess.round4dp}
                onChange={(e) =>
                  updateDraft((p) => ({
                    ...p,
                    postProcess: {
                      ...p.postProcess,
                      round4dp: e.target.checked,
                    },
                  }))
                }
              />
            }
            label="Round to 4 decimal places"
          />
          <InfoTooltip title={FIELD_TOOLTIPS.round4dp} />
        </Box>
      </StepSection>

      <FormulaEditorModal
        open={formulaModalOpen}
        initialFormula={draft.formula}
        endpoint={endpoint}
        signals={signals}
        clamp01={draft.postProcess.clamp01}
        onClose={() => setFormulaModalOpen(false)}
        onApply={(formula) => updateDraft((prev) => ({ ...prev, formula }))}
      />
    </Box>
  );
};

export default RecommendationEnginePipelineEditor;
